/**
 * Cliente HTTP: peticiones autenticadas con cookies httpOnly (mismo origen).
 * En 401 intenta POST /refresh y reintenta una vez.
 */
import { endSessionAndGoToLogin, refreshSession } from '@/services/sessionRefresh';

export class ApiError extends Error {
    status: number;
    constructor(message: string, status: number) {
        super(message);
        this.status = status;
    }
}

function errMessageFromBody(data: unknown): string | undefined {
    if (data && typeof data === 'object' && 'message' in data) {
        const m = (data as { message: unknown }).message;
        if (typeof m === 'string' && m.trim()) return m;
    }
    return undefined;
}

async function readResponseBodyJson(response: Response): Promise<unknown> {
    const text = await response.text();
    if (!text.trim()) {
        if (!response.ok) return null;
        throw new ApiError('El servidor devolvió una respuesta vacía', response.status);
    }
    try {
        return JSON.parse(text) as unknown;
    } catch {
        throw new ApiError('Respuesta no válida del servidor', response.status);
    }
}

async function fetchWithCredentials(
    url: string,
    init: RequestInit,
): Promise<Response> {
    return fetch(url, { ...init, credentials: 'include' });
}

export async function apiRequest<T = unknown>(
    path: string,
    { method = 'GET', body }: { method?: string; body?: unknown } = {},
): Promise<T> {
    const headers: Record<string, string> = {};
    if (body !== undefined) {
        headers['Content-Type'] = 'application/json';
    }

    const url = path.startsWith('/') ? path : `/${path}`;
    const init: RequestInit = {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
    };

    let response = await fetchWithCredentials(url, init);

    if (response.status === 401 && path !== '/refresh' && path !== '/login') {
        const renewed = await refreshSession();
        if (renewed) {
            response = await fetchWithCredentials(url, init);
        }
    }

    if (method === 'DELETE' && response.ok) {
        return undefined as T;
    }

    if (response.status === 401) {
        void endSessionAndGoToLogin();
        throw new ApiError('Sesión expirada', 401);
    }

    const data = await readResponseBodyJson(response);

    if (!response.ok) {
        throw new ApiError(errMessageFromBody(data) || 'Error del servidor', response.status);
    }

    return data as T;
}
